import type { ModuleActivation, ModuleType } from "@/db/schema/module";
import type { MvActivityType } from "@/db/schema/movement";

// ---------------------------------------------------------------------------
// Config types — one variant per module type
// ---------------------------------------------------------------------------

export type MovementModuleConfig = {
  kind: "movement";
  activityType: MvActivityType;
};

/** Union of all module configs. Extend when adding new module types. */
export type ModuleConfig = MovementModuleConfig;

// ---------------------------------------------------------------------------
// Module option catalog (replaces EVENT_FEATURE_DEFINITIONS)
// ---------------------------------------------------------------------------

export type ModuleOption = {
  moduleType: ModuleType;
  name: string;
  tabLabel: string;
  hint: string;
  config: ModuleConfig;
};

const MODULE_OPTIONS: ModuleOption[] = [
  {
    moduleType: "movement",
    name: "Movement Run",
    tabLabel: "Movement",
    hint: "Uses the Movement mini app for running.",
    config: { kind: "movement", activityType: "run" },
  },
  {
    moduleType: "movement",
    name: "Movement Walk",
    tabLabel: "Movement",
    hint: "Uses the Movement mini app for walking.",
    config: { kind: "movement", activityType: "walk" },
  },
  {
    moduleType: "movement",
    name: "Movement Trekking",
    tabLabel: "Movement",
    hint: "Uses the Movement mini app for trekking.",
    config: { kind: "movement", activityType: "hike" },
  },
  {
    moduleType: "movement",
    name: "Movement Ride",
    tabLabel: "Movement",
    hint: "Uses the Movement mini app for cycling.",
    config: { kind: "movement", activityType: "ride" },
  },
];

// ---------------------------------------------------------------------------
// Module catalog — grouped by module type for UI rendering
// ---------------------------------------------------------------------------

export type ModuleActivityVariant = {
  key: string;
  label: string;
  activityType: MvActivityType;
};

export type ModuleCatalogEntry = {
  moduleType: ModuleType;
  name: string;
  tabLabel: string;
  description: string;
  variants: ModuleActivityVariant[];
};

const MODULE_CATALOG: ModuleCatalogEntry[] = [
  {
    moduleType: "movement",
    name: "Movement",
    tabLabel: "Movement",
    description:
      "Track runs, walks, hikes, or rides using the Movement mini app. Participants start an activity and completions are recorded automatically within the active window.",
    variants: [
      { key: "movement_run", label: "Run", activityType: "run" },
      { key: "movement_walk", label: "Walk", activityType: "walk" },
      { key: "movement_hike", label: "Hike", activityType: "hike" },
      { key: "movement_ride", label: "Ride", activityType: "ride" },
    ],
  },
];

export function listModuleCatalog(): ModuleCatalogEntry[] {
  return MODULE_CATALOG;
}

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function listModuleOptions(): ModuleOption[] {
  return MODULE_OPTIONS;
}

/** Build a stable key for a module option (used as form value / URL param). */
export function moduleOptionKey(option: ModuleOption): string {
  if (option.config.kind === "movement") {
    return `movement_${option.config.activityType}`;
  }
  return option.moduleType;
}

/** Find a module option by its key. */
export function getModuleOptionByKey(key: string): ModuleOption | null {
  return MODULE_OPTIONS.find((opt) => moduleOptionKey(opt) === key) ?? null;
}

// ---------------------------------------------------------------------------
// Activation-level helpers (work on DB rows)
// ---------------------------------------------------------------------------

export type ResolvedModule = {
  activationId: number;
  moduleType: ModuleType;
  name: string;
  tabLabel: string;
  hint: string;
  config: ModuleConfig;
  startsAt: Date | null;
  endsAt: Date | null;
};

/** Resolve a moduleActivation row into a displayable module. */
export function resolveModuleActivation(
  activation: ModuleActivation | null | undefined,
): ResolvedModule | null {
  if (!activation) return null;

  const config = activation.config as ModuleConfig;
  if (!config?.kind) return null;

  const option = MODULE_OPTIONS.find(
    (opt) =>
      opt.moduleType === activation.moduleType &&
      opt.config.kind === config.kind &&
      (config.kind === "movement" && opt.config.kind === "movement"
        ? opt.config.activityType === config.activityType
        : true),
  );

  return option
    ? {
        activationId: activation.id,
        moduleType: activation.moduleType,
        name: option.name,
        tabLabel: option.tabLabel,
        hint: option.hint,
        config,
        startsAt: activation.startsAt,
        endsAt: activation.endsAt,
      }
    : null;
}

/** Get the tab label for an activation, with fallback. */
export function resolveModuleTabLabel(
  module: ResolvedModule | null | undefined,
): string {
  return module?.tabLabel ?? "Modules";
}

/** Narrow to a movement config if applicable. */
export function getMovementModuleConfig(
  module: ResolvedModule | null | undefined,
): MovementModuleConfig | null {
  if (!module) return null;
  return module.config.kind === "movement" ? module.config : null;
}

/** Check if a module is currently active based on its time window. */
export function isModuleActive(
  module: ResolvedModule | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!module) return false;
  if (module.startsAt && now < module.startsAt) return false;
  if (module.endsAt && now > module.endsAt) return false;
  return true;
}
